import React, { useMemo } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Close,
  LocalShippingOutlined,
  RadioButtonChecked as RadioButtonCheckedIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from "@mui/icons-material";
import { observer } from "mobx-react";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import SkuCopyRow from "../../../../../views/components/common/SkuCopyRow";
import {
  getSwayyShipmentStatusLabel,
  getSwayyShipmentStatusTone,
} from "../../../../../models/SwayyShipment/SwayyShipmentFilters";
import {
  getSwayyShipmentNumber,
  SwayyShipment,
  SwayyShipmentAuditDetails,
  SwayyShipmentAuditEvent,
  SwayyShipmentInventoryMovement,
} from "../../../../../models/SwayyShipment/SwayyShipment";
import { swayyShipmentStore } from "../../../stores/SwayyShipmentStore";
import { swayyColors, swayyFont, swayyRadii } from "../../../theme/tokens";

/** Above AppBar (`theme.zIndex.drawer + 1`). */
const DRAWER_Z_OFFSET = 2000;

interface SwayyShipmentDetailDrawerProps {
  onOpenOrder: (orderId: number) => void;
}

type TimelineStepState = "completed" | "active" | "pending";
type TimelineStepVariant = "default" | "negative";

interface TimelineStep {
  key: string;
  label: string;
  date: string | null;
  state: TimelineStepState;
  variant: TimelineStepVariant;
}

const POSITIVE_STATUS_PATH = [
  "created",
  "picked_up",
  "in_transit",
  "out_for_delivery",
  "delivered",
] as const;

const NEGATIVE_STATUSES = new Set([
  "failed_delivery",
  "rto_initiated",
  "rto_delivered",
]);

const sectionPaperSx = {
  p: { xs: 1.5, sm: 2 },
  borderRadius: swayyRadii.md,
  border: `1px solid ${swayyColors.border}`,
  bgcolor: swayyColors.white,
  boxShadow: "0 8px 28px rgba(0, 81, 84, 0.045)",
};

const thSx = {
  fontSize: "0.72rem",
  fontWeight: 700,
  color: swayyColors.textSecondary,
  letterSpacing: "0.04em",
  textTransform: "uppercase" as const,
  py: 1.25,
  bgcolor: swayyColors.pineLight,
  borderBottom: `1px solid ${swayyColors.border}`,
  fontFamily: swayyFont,
  whiteSpace: "nowrap" as const,
};

const tdSx = {
  fontSize: 13,
  color: swayyColors.deepGreen,
  fontWeight: 500,
  py: 1.25,
  borderBottom: `1px solid ${swayyColors.border}`,
  fontFamily: swayyFont,
};

const StatusChip = ({
  label,
  tone = "pine",
}: {
  label: string;
  tone?: "pine" | "success" | "warning" | "danger" | "muted";
}) => {
  const styles: Record<string, { color: string; border: string }> = {
    pine: { color: swayyColors.deepGreen, border: swayyColors.deepGreen },
    success: { color: swayyColors.success, border: swayyColors.success },
    warning: { color: swayyColors.warning, border: swayyColors.warning },
    danger: { color: swayyColors.danger, border: swayyColors.danger },
    muted: { color: swayyColors.textSecondary, border: swayyColors.border },
  };
  const toneStyle = styles[tone];

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1.1,
        py: 0.3,
        borderRadius: swayyRadii.sm,
        border: `1px solid ${toneStyle.border}`,
        bgcolor: swayyColors.white,
        color: toneStyle.color,
        fontSize: 12,
        fontWeight: 600,
        fontFamily: swayyFont,
        lineHeight: 1.4,
        textTransform: "capitalize",
        whiteSpace: "nowrap",
      }}
    >
      {label.replace(/_/g, " ")}
    </Box>
  );
};

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      gap: { xs: 0.25, sm: 2 },
      py: 0.85,
    }}
  >
    <CustomTypography
      variant="body2"
      color={swayyColors.textSecondary}
      sx={{
        fontFamily: swayyFont,
        fontSize: 13,
        minWidth: { sm: 140 },
        flexShrink: 0,
      }}
    >
      {label}
    </CustomTypography>
    <Box
      sx={{
        minWidth: 0,
        flex: 1,
        fontFamily: swayyFont,
        fontSize: 13,
        fontWeight: 600,
        color: swayyColors.deepGreen,
        wordBreak: "break-word",
      }}
    >
      {typeof value === "string" || typeof value === "number" ? (
        <CustomTypography
          variant="body2"
          color={swayyColors.deepGreen}
          sx={{ fontFamily: swayyFont, fontSize: 13, fontWeight: 600 }}
        >
          {value}
        </CustomTypography>
      ) : (
        value
      )}
    </Box>
  </Box>
);

const getMilestoneDate = (
  shipment: SwayyShipment,
  status: string,
): string | null => {
  switch (status) {
    case "created":
      return shipment.created_at ?? null;
    case "picked_up":
    case "in_transit":
      return shipment.shipped_at ?? null;
    case "out_for_delivery":
      return shipment.shipped_at ?? null;
    case "delivered":
      return shipment.delivered_at ?? null;
    case "failed_delivery":
    case "rto_initiated":
    case "rto_delivered":
      return shipment.updated_at ?? shipment.shipped_at ?? null;
    default:
      return null;
  }
};

const getTimelinePath = (status: string): string[] => {
  const current = (status || "created").toLowerCase();

  if (!NEGATIVE_STATUSES.has(current)) {
    return [...POSITIVE_STATUS_PATH];
  }

  if (current === "failed_delivery") {
    return [
      "created",
      "picked_up",
      "in_transit",
      "out_for_delivery",
      "failed_delivery",
    ];
  }

  if (current === "rto_initiated") {
    return ["created", "picked_up", "in_transit", "rto_initiated"];
  }

  if (current === "rto_delivered") {
    return [
      "created",
      "picked_up",
      "in_transit",
      "rto_initiated",
      "rto_delivered",
    ];
  }

  return ["created", current];
};

const getStepState = (
  statusId: string,
  currentStatus: string,
  path: string[],
): TimelineStepState => {
  const stepIndex = path.indexOf(statusId);
  const currentIndex = path.indexOf(currentStatus);

  if (stepIndex === -1) return "pending";
  if (stepIndex < currentIndex) return "completed";
  if (stepIndex === currentIndex) {
    if (NEGATIVE_STATUSES.has(currentStatus) || currentStatus === "delivered") {
      return "completed";
    }
    return "active";
  }
  return "pending";
};

const buildTimelineSteps = (shipment: SwayyShipment): TimelineStep[] => {
  const current = String(shipment.status || "created").toLowerCase();
  const path = getTimelinePath(current);

  return path.map((statusId) => ({
    key: statusId,
    label: getSwayyShipmentStatusLabel(statusId),
    date: getMilestoneDate(shipment, statusId),
    state: getStepState(statusId, current, path),
    variant: NEGATIVE_STATUSES.has(statusId) ? "negative" : "default",
  }));
};

const getStepColor = (step: TimelineStep): string => {
  if (step.variant === "negative" && step.state !== "pending") {
    return swayyColors.danger;
  }
  if (step.state === "pending") return swayyColors.textSecondary;
  return swayyColors.pine;
};

const TimelineIcon = ({
  step,
  color,
}: {
  step: TimelineStep;
  color: string;
}) => {
  if (step.variant === "negative" && step.state !== "pending") {
    return <CancelIcon sx={{ color, fontSize: { xs: 24, sm: 28 } }} />;
  }
  if (step.state === "completed") {
    return <CheckCircleIcon sx={{ color, fontSize: { xs: 24, sm: 28 } }} />;
  }
  if (step.state === "active") {
    return (
      <RadioButtonCheckedIcon sx={{ color, fontSize: { xs: 24, sm: 28 } }} />
    );
  }
  return (
    <RadioButtonUncheckedIcon
      sx={{ color: swayyColors.border, fontSize: { xs: 24, sm: 28 } }}
    />
  );
};

const ShipmentTimeline = ({ shipment }: { shipment: SwayyShipment }) => {
  const steps = useMemo(() => buildTimelineSteps(shipment), [shipment]);

  return (
    <Paper
      elevation={0}
      sx={{
        ...sectionPaperSx,
        background: `linear-gradient(135deg, ${swayyColors.white} 0%, ${swayyColors.pineLight} 100%)`,
      }}
    >
      <CustomTypography
        variant="heading7"
        color={swayyColors.deepGreen}
        sx={{
          fontFamily: swayyFont,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          fontSize: 12,
          mb: { xs: 1.5, sm: 2 },
        }}
      >
        Shipment Timeline
      </CustomTypography>

      {/* Mobile: vertical */}
      <Box sx={{ display: { xs: "flex", sm: "none" }, flexDirection: "column" }}>
        {steps.map((step, index) => {
          const color = getStepColor(step);
          const isLast = index === steps.length - 1;
          return (
            <Box
              key={step.key}
              sx={{
                display: "flex",
                gap: 1.5,
                position: "relative",
                pb: isLast ? 0 : 2,
              }}
            >
              {!isLast ? (
                <Box
                  sx={{
                    position: "absolute",
                    left: 13,
                    top: 28,
                    bottom: 0,
                    width: 2,
                    bgcolor:
                      step.state === "completed"
                        ? color
                        : swayyColors.border,
                  }}
                />
              ) : null}
              <Box sx={{ zIndex: 1, flexShrink: 0 }}>
                <TimelineIcon step={step} color={color} />
              </Box>
              <Box sx={{ minWidth: 0, pt: 0.25 }}>
                <CustomTypography
                  variant="body2"
                  color={
                    step.variant === "negative" && step.state !== "pending"
                      ? swayyColors.danger
                      : swayyColors.deepGreen
                  }
                  sx={{ fontFamily: swayyFont, fontWeight: 700, fontSize: 13 }}
                >
                  {step.label}
                </CustomTypography>
                <CustomTypography
                  variant="body3"
                  color={
                    step.state === "pending"
                      ? swayyColors.border
                      : swayyColors.textSecondary
                  }
                  sx={{
                    fontFamily: swayyFont,
                    fontSize: 12,
                    display: "block",
                    mt: 0.25,
                  }}
                >
                  {step.date ? formatDate(step.date) : "Pending"}
                </CustomTypography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Desktop: horizontal */}
      <Box
        sx={{
          display: { xs: "none", sm: "block" },
          overflowX: "auto",
          pb: 0.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            minWidth: "max-content",
          }}
        >
          {steps.map((step, index) => {
            const color = getStepColor(step);
            const isLast = index === steps.length - 1;
            const connectorCompleted =
              step.state === "completed" ||
              (steps[index + 1]?.state !== "pending" && !isLast);

            return (
              <Box
                key={step.key}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  flex: "0 0 auto",
                  minWidth: { sm: 120, md: 140 },
                  maxWidth: { sm: 150, md: 170 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    px: 0.75,
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center", width: "100%" }}
                  >
                    {index > 0 ? (
                      <Box
                        sx={{
                          flex: 1,
                          height: 2,
                          bgcolor: connectorCompleted
                            ? color
                            : swayyColors.border,
                          mr: 0.5,
                        }}
                      />
                    ) : (
                      <Box sx={{ flex: 1, mr: 0.5 }} />
                    )}
                    <TimelineIcon step={step} color={color} />
                    {!isLast ? (
                      <Box
                        sx={{
                          flex: 1,
                          height: 2,
                          bgcolor: connectorCompleted
                            ? color
                            : swayyColors.border,
                          ml: 0.5,
                        }}
                      />
                    ) : (
                      <Box sx={{ flex: 1, ml: 0.5 }} />
                    )}
                  </Box>
                  <CustomTypography
                    variant="body2"
                    color={
                      step.variant === "negative" && step.state !== "pending"
                        ? swayyColors.danger
                        : swayyColors.deepGreen
                    }
                    sx={{
                      fontFamily: swayyFont,
                      fontWeight: 700,
                      fontSize: 12,
                      mt: 1,
                      px: 0.5,
                    }}
                  >
                    {step.label}
                  </CustomTypography>
                  <CustomTypography
                    variant="body3"
                    color={
                      step.state === "pending"
                        ? swayyColors.border
                        : swayyColors.textSecondary
                    }
                    sx={{
                      fontFamily: swayyFont,
                      fontSize: 11,
                      display: "block",
                      mt: 0.25,
                      px: 0.5,
                    }}
                  >
                    {step.date ? formatDate(step.date) : "Pending"}
                  </CustomTypography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Paper>
  );
};

const formatSignedQty = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return "—";
  if (value > 0) return `+${value}`;
  return String(value);
};

const formatMovementType = (type: string | null | undefined): string => {
  if (!type) return "—";
  return type.replace(/_/g, " ");
};

const formatAuditAction = (action: string | null | undefined): string => {
  if (!action) return "Event";
  return action
    .split(".")
    .map((part) =>
      part
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
    )
    .join(" · ");
};

const formatAuditFieldName = (field: string): string =>
  field
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const formatAuditValue = (value: unknown): string => {
  if (value == null || value === "") return "—";
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    if (
      !Number.isNaN(parsed) &&
      /^\d{4}-\d{2}-\d{2}T/.test(value)
    ) {
      return formatDate(value);
    }
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const getAuditDetailLines = (
  details: SwayyShipmentAuditDetails | null | undefined,
): string[] => {
  if (!details || typeof details !== "object") return [];

  const lines: string[] = [];

  if (details.order_id != null) {
    lines.push(`Order #${details.order_id}`);
  }
  if (details.carrier_id != null) {
    lines.push(`Carrier #${details.carrier_id}`);
  } else if (details.carrier_id === null && "carrier_id" in details) {
    lines.push("Carrier —");
  }
  if ("awb" in details) {
    lines.push(details.awb ? `AWB ${details.awb}` : "AWB —");
  }
  if (Array.isArray(details.items) && details.items.length > 0) {
    const totalQty = details.items.reduce(
      (sum, item) => sum + (item.qty || 0),
      0,
    );
    lines.push(
      `${details.items.length} item${details.items.length === 1 ? "" : "s"} · Qty ${totalQty}`,
    );
  }
  if (details.diff && typeof details.diff === "object") {
    Object.entries(details.diff).forEach(([field, change]) => {
      const fromValue = formatAuditValue(change?.from);
      const toValue = formatAuditValue(change?.to);
      if (fromValue === toValue) {
        lines.push(`${formatAuditFieldName(field)}: ${toValue}`);
      } else {
        lines.push(
          `${formatAuditFieldName(field)}: ${fromValue} → ${toValue}`,
        );
      }
    });
  }

  return lines;
};

const InventoryMovementsTable = ({
  movements,
}: {
  movements: SwayyShipmentInventoryMovement[];
}) => (
  <TableContainer
    component={Paper}
    elevation={0}
    sx={{
      ...sectionPaperSx,
      p: 0,
      overflowX: "auto",
    }}
  >
    <Table size="small" sx={{ minWidth: 640 }}>
      <TableHead>
        <TableRow>
          {[
            "Type",
            "Variant",
            "Warehouse",
            "On Hand",
            "Reserved",
            "Order Item",
            "Actor",
            "Reason",
            "Date",
          ].map((header) => (
            <TableCell key={header} align="center" sx={thSx}>
              {header}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {movements.map((movement) => (
          <TableRow
            key={movement.id}
            sx={{ "&:hover": { bgcolor: swayyColors.pineLight } }}
          >
            <TableCell align="center" sx={tdSx}>
              <StatusChip
                label={formatMovementType(movement.movement_type)}
                tone="pine"
              />
            </TableCell>
            <TableCell align="center" sx={tdSx}>
              #{movement.variant_id}
            </TableCell>
            <TableCell align="center" sx={tdSx}>
              #{movement.warehouse_id}
            </TableCell>
            <TableCell
              align="center"
              sx={{
                ...tdSx,
                fontWeight: 700,
                color:
                  movement.qty_on_hand < 0
                    ? swayyColors.danger
                    : movement.qty_on_hand > 0
                      ? swayyColors.success
                      : swayyColors.deepGreen,
              }}
            >
              {formatSignedQty(movement.qty_on_hand)}
            </TableCell>
            <TableCell
              align="center"
              sx={{
                ...tdSx,
                fontWeight: 700,
                color:
                  movement.qty_reserved < 0
                    ? swayyColors.danger
                    : movement.qty_reserved > 0
                      ? swayyColors.success
                      : swayyColors.deepGreen,
              }}
            >
              {formatSignedQty(movement.qty_reserved)}
            </TableCell>
            <TableCell align="center" sx={tdSx}>
              {movement.order_item_id != null
                ? `#${movement.order_item_id}`
                : "—"}
              {movement.order_id != null ? (
                <CustomTypography
                  variant="body3"
                  color={swayyColors.textSecondary}
                  sx={{
                    fontFamily: swayyFont,
                    fontSize: 11,
                    display: "block",
                    mt: 0.25,
                  }}
                >
                  Order #{movement.order_id}
                </CustomTypography>
              ) : null}
            </TableCell>
            <TableCell align="center" sx={tdSx}>
              <CustomTypography
                variant="body2"
                color={swayyColors.deepGreen}
                sx={{
                  fontFamily: swayyFont,
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: "capitalize",
                }}
              >
                {movement.actor_type || "—"}
              </CustomTypography>
              {movement.actor_id != null ? (
                <CustomTypography
                  variant="body3"
                  color={swayyColors.textSecondary}
                  sx={{
                    fontFamily: swayyFont,
                    fontSize: 11,
                    display: "block",
                  }}
                >
                  #{movement.actor_id}
                </CustomTypography>
              ) : null}
            </TableCell>
            <TableCell align="center" sx={tdSx}>
              {movement.reason || "—"}
            </TableCell>
            <TableCell align="center" sx={{ ...tdSx, whiteSpace: "nowrap" }}>
              {movement.created_at ? formatDate(movement.created_at) : "—"}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const AuditTrailList = ({
  events,
}: {
  events: SwayyShipmentAuditEvent[];
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
    {events.map((event) => {
      const detailLines = getAuditDetailLines(event.details);
      const statusTone =
        event.status === "success"
          ? "success"
          : event.status === "failed" || event.status === "error"
            ? "danger"
            : "muted";

      return (
        <Box
          key={event.id}
          sx={{
            p: 1.5,
            borderRadius: swayyRadii.md,
            border: `1px solid ${swayyColors.border}`,
            bgcolor: swayyColors.pageBg,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 1,
              mb: 0.75,
            }}
          >
            <Box minWidth={0}>
              <CustomTypography
                variant="body2"
                color={swayyColors.deepGreen}
                sx={{
                  fontFamily: swayyFont,
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                {formatAuditAction(event.action)}
              </CustomTypography>
              <CustomTypography
                variant="body3"
                color={swayyColors.textSecondary}
                sx={{
                  fontFamily: swayyFont,
                  fontSize: 11,
                  mt: 0.25,
                  textTransform: "capitalize",
                }}
              >
                {[
                  event.entity_type
                    ? `${event.entity_type}${
                        event.entity_id != null ? ` #${event.entity_id}` : ""
                      }`
                    : null,
                  event.actor_type
                    ? `${event.actor_type}${
                        event.actor_admin_id != null
                          ? ` #${event.actor_admin_id}`
                          : ""
                      }`
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </CustomTypography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: 0.5,
                flexShrink: 0,
              }}
            >
              {event.status ? (
                <StatusChip label={event.status} tone={statusTone} />
              ) : null}
              <CustomTypography
                variant="body3"
                color={swayyColors.textSecondary}
                sx={{ fontFamily: swayyFont, fontSize: 11, whiteSpace: "nowrap" }}
              >
                {event.created_at ? formatDate(event.created_at) : "—"}
              </CustomTypography>
            </Box>
          </Box>

          {detailLines.length > 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.35,
                pt: 0.5,
                borderTop: `1px dashed ${swayyColors.border}`,
              }}
            >
              {detailLines.map((line) => (
                <CustomTypography
                  key={`${event.id}-${line}`}
                  variant="body3"
                  color={swayyColors.deepGreen}
                  sx={{
                    fontFamily: swayyFont,
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  {line}
                </CustomTypography>
              ))}
            </Box>
          ) : null}
        </Box>
      );
    })}
  </Box>
);

const ItemsTable = ({ shipment }: { shipment: SwayyShipment }) => {
  const items = shipment.items ?? [];

  if (!items.length) {
    return (
      <Box
        sx={{
          py: 4,
          border: `1px dashed ${swayyColors.border}`,
          borderRadius: swayyRadii.md,
          textAlign: "center",
          bgcolor: swayyColors.pageBg,
        }}
      >
        <CustomTypography
          variant="body2"
          color={swayyColors.textSecondary}
          sx={{ fontFamily: swayyFont }}
        >
          No items found for this shipment.
        </CustomTypography>
      </Box>
    );
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        ...sectionPaperSx,
        p: 0,
        overflowX: "auto",
      }}
    >
      <Table size="small" sx={{ minWidth: 480 }}>
        <TableHead>
          <TableRow>
            {["#", "Product", "SKU", "Qty"].map((header) => (
              <TableCell key={header} align="center" sx={thSx}>
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, index) => (
            <TableRow
              key={`${item.order_item_id}-${item.sku}-${index}`}
              sx={{ "&:hover": { bgcolor: swayyColors.pineLight } }}
            >
              <TableCell align="center" sx={tdSx}>
                {index + 1}
              </TableCell>
              <TableCell align="left" sx={{ ...tdSx, fontWeight: 700 }}>
                {item.name || "—"}
              </TableCell>
              <TableCell align="center" sx={tdSx}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <SkuCopyRow
                    sku={item.sku}
                    emptyFallback="—"
                    copyLabel="Copy SKU"
                    color={swayyColors.textSecondary}
                    variant="body3"
                    allowWrap
                    typographySx={{
                      fontFamily: swayyFont,
                      fontSize: 12,
                      textAlign: "center",
                    }}
                  />
                </Box>
              </TableCell>
              <TableCell align="center" sx={tdSx}>
                {item.qty}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const SwayyShipmentDetailDrawer: React.FC<SwayyShipmentDetailDrawerProps> = ({
  onOpenOrder,
}) => {
  const store = swayyShipmentStore;
  const shipment = store.detail?.shipment;
  const auditTrail = store.detail?.audit_trail ?? [];
  const inventoryMovements = store.detail?.inventory_movements ?? [];

  return (
    <Drawer
      anchor="right"
      open={store.detailOpen}
      onClose={store.closeDetail}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 560, md: 720 },
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          bgcolor: swayyColors.white,
          borderRadius: 0,
        },
      }}
      ModalProps={{
        keepMounted: true,
        sx: {
          zIndex: (muiTheme) => muiTheme.zIndex.drawer + DRAWER_Z_OFFSET,
        },
        slotProps: {
          backdrop: {
            sx: {
              zIndex: (muiTheme) =>
                muiTheme.zIndex.drawer + DRAWER_Z_OFFSET - 1,
            },
          },
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 1.5, sm: 2 },
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          borderBottom: `1px solid ${swayyColors.border}`,
          background: `linear-gradient(135deg, ${swayyColors.white} 0%, ${swayyColors.pineLight} 100%)`,
        }}
      >
        <Box display="flex" gap={1.5} alignItems="center" minWidth={0}>
          <LocalShippingOutlined
            sx={{ flexShrink: 0, color: swayyColors.pine }}
          />
          <Box minWidth={0}>
            <CustomTypography
              variant="heading5"
              color={swayyColors.deepGreen}
              sx={{
                fontFamily: swayyFont,
                fontWeight: 700,
                fontSize: { xs: "1rem", sm: "1.25rem" },
              }}
            >
              Shipment Details
            </CustomTypography>
            <CustomTypography
              variant="body2"
              color={swayyColors.textSecondary}
              sx={{ fontFamily: swayyFont, mt: 0.75, display: "block" }}
            >
              {shipment
                ? getSwayyShipmentNumber(shipment)
                : "Shipment information"}
            </CustomTypography>
          </Box>
        </Box>
        <IconButton
          onClick={store.closeDetail}
          size="small"
          aria-label="Close"
        >
          <Close />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 1.5, sm: 2 },
          display: "flex",
          flexDirection: "column",
          gap: { xs: 1.5, sm: 2 },
        }}
      >
        {store.detailLoading || !shipment ? (
          <Box
            sx={{
              minHeight: 320,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress sx={{ color: swayyColors.pine }} />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                alignItems: "center",
              }}
            >
              <StatusChip
                label={getSwayyShipmentStatusLabel(String(shipment.status))}
                tone={getSwayyShipmentStatusTone(String(shipment.status))}
              />
              {shipment.invoice ? (
                <StatusChip
                  label={`Invoice #${shipment.invoice.id} · ${shipment.invoice.status}`}
                  tone="muted"
                />
              ) : null}
            </Box>

            <ShipmentTimeline shipment={shipment} />

            <Box>
              <CustomTypography
                variant="heading7"
                color={swayyColors.deepGreen}
                sx={{
                  fontFamily: swayyFont,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  fontSize: 12,
                  mb: 1.5,
                }}
              >
                Items ({(shipment.items ?? []).length})
              </CustomTypography>
              <ItemsTable shipment={shipment} />
            </Box>

            <Paper elevation={0} sx={sectionPaperSx}>
              <CustomTypography
                variant="heading7"
                color={swayyColors.deepGreen}
                sx={{
                  fontFamily: swayyFont,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  fontSize: 12,
                  mb: 1,
                }}
              >
                Shipment Overview
              </CustomTypography>
              <Divider sx={{ mb: 1, borderColor: swayyColors.border }} />
              <DetailRow
                label="Shipment ID"
                value={getSwayyShipmentNumber(shipment)}
              />
              <DetailRow
                label="Order"
                value={
                  <Link
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() => onOpenOrder(shipment.order_id)}
                    sx={{
                      fontFamily: swayyFont,
                      fontSize: 13,
                      fontWeight: 700,
                      color: swayyColors.deepGreen,
                      textAlign: "left",
                    }}
                  >
                    {shipment.order_number || `#${shipment.order_id}`}
                  </Link>
                }
              />
              <DetailRow label="Carrier" value={shipment.carrier || "—"} />
              <DetailRow
                label="AWB"
                value={
                  shipment.awb ? (
                    shipment.tracking_url ? (
                      <Link
                        href={shipment.tracking_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontFamily: swayyFont,
                          fontWeight: 700,
                          color: swayyColors.pine,
                        }}
                      >
                        {shipment.awb}
                      </Link>
                    ) : (
                      shipment.awb
                    )
                  ) : (
                    "—"
                  )
                }
              />
              <DetailRow
                label="Warehouse"
                value={
                  shipment.warehouse_id != null
                    ? `#${shipment.warehouse_id}`
                    : "—"
                }
              />
              <DetailRow
                label="Expected"
                value={
                  shipment.expected_at ? formatDate(shipment.expected_at) : "—"
                }
              />
              <DetailRow
                label="Shipped"
                value={
                  shipment.shipped_at ? formatDate(shipment.shipped_at) : "—"
                }
              />
              <DetailRow
                label="Delivered"
                value={
                  shipment.delivered_at
                    ? formatDate(shipment.delivered_at)
                    : "—"
                }
              />
              <DetailRow
                label="Created"
                value={
                  shipment.created_at ? formatDate(shipment.created_at) : "—"
                }
              />
              <DetailRow
                label="Last Updated"
                value={
                  shipment.updated_at ? formatDate(shipment.updated_at) : "—"
                }
              />
            </Paper>

            {inventoryMovements.length > 0 && (
              <Box>
                <CustomTypography
                  variant="heading7"
                  color={swayyColors.deepGreen}
                  sx={{
                    fontFamily: swayyFont,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    fontSize: 12,
                    mb: 1.5,
                  }}
                >
                  Inventory Movements ({inventoryMovements.length})
                </CustomTypography>
                <InventoryMovementsTable movements={inventoryMovements} />
              </Box>
            )}

            {auditTrail.length > 0 && (
              <Box>
                <CustomTypography
                  variant="heading7"
                  color={swayyColors.deepGreen}
                  sx={{
                    fontFamily: swayyFont,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    fontSize: 12,
                    mb: 1.5,
                  }}
                >
                  Audit Trail ({auditTrail.length})
                </CustomTypography>
                <AuditTrailList events={auditTrail} />
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1,
                pt: 0.5,
              }}
            >
              {shipment.tracking_url ? (
                <AppButton
                  label="Track package"
                  onClick={() =>
                    window.open(shipment.tracking_url || "", "_blank")
                  }
                  customStyles={{
                    borderRadius: swayyRadii.md,
                    bgcolor: swayyColors.pine,
                    color: swayyColors.white,
                    flex: 1,
                    "&:hover": { bgcolor: swayyColors.pineHover },
                  }}
                />
              ) : null}
              <AppButton
                label="Open order"
                onClick={() => onOpenOrder(shipment.order_id)}
                customStyles={{
                  borderRadius: swayyRadii.md,
                  bgcolor: "transparent",
                  color: swayyColors.deepGreen,
                  border: `1px solid ${swayyColors.border}`,
                  flex: 1,
                  "&:hover": { bgcolor: swayyColors.pineLight },
                }}
              />
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default observer(SwayyShipmentDetailDrawer);
